import { AxiosInstance } from "axios";
import querystring from "query-string";
import {
  IKLObservationIdentifierNew,
  IObservationIdentifierResponse,
  IObservationIndetifiersBulkNew,
  ObservationIdentifiersSearch,
} from "./types";
import { ApiV2Error } from "../../interfaces/Api";

const ERROR_CONFIG = {
  DuplicateMacAddress: {
    type: "validateUniqueness",
    resolveKey: (row: any) => row.macAddress.toLowerCase(),
    field: "macAddress",
  },
  NotUniqueObservationIdentifier: {
    type: "validateGlobalUniqueness",
    resolveKey: (row: any) =>
      `${row.macAddress.toLowerCase()}_${row.antennaDirection}`,
    field: "observationIdentifier",
  },
} as const;

type ErrorType = keyof typeof ERROR_CONFIG;

type EditErrorType = {
  field: "macAddress" | "observationIdentifier";
  type: "validateUniqueness" | "validateGlobalUniqueness";
  message: string;
};

type CreateErrorType = {
  field:
    | `dataDefinitions.${number}.macAddress`
    | `dataDefinitions.${number}.observationIdentifier`;
  type: "validateUniqueness" | "validateGlobalUniqueness";
  message: string;
};

const getApiError = (error: any): ApiV2Error | null => {
  const errors = error?.response?.data?.errors;
  return Array.isArray(errors) ? errors[0] : null;
};

const resolveEditError = (error: any): EditErrorType | null => {
  const apiError = getApiError(error);
  if (!apiError) return null;

  const config = ERROR_CONFIG[apiError.errorMessage as ErrorType] ?? null;
  if (!config) return null;

  return {
    field: config.field,
    type: config.type,
    message: apiError.errorDetails?.attemptedValue || "",
  };
};

const resolveCreateError = (
  values: IKLObservationIdentifierNew[],
  error: any,
): CreateErrorType[] => {
  const apiError = getApiError(error);
  if (!apiError) return [];

  const config = ERROR_CONFIG[apiError.errorMessage as ErrorType] ?? null;
  const attempted = apiError.errorDetails?.attemptedValue;
  if (!config || !Array.isArray(attempted) || attempted.length === 0) return [];

  return values
    .map((row, index) => {
      const key = config.resolveKey(row);
      if (!attempted.includes(key)) return null;

      return {
        field: `dataDefinitions.${index}.${config.field}`,
        type: config.type,
        message: key,
      };
    })
    .filter(Boolean) as CreateErrorType[];
};

const mapToObservationIdentifierResponse = (
  data: any,
): IObservationIdentifierResponse => ({
  dataDefinitions: data.klDataDefinitionsOverview || data.klDataDefinitions,
  page: data.page,
  pageSize: data.pageSize,
  pages: data.pages,
  total: data.total,
});

const getObservationIdentifiers = async (
  query: ObservationIdentifiersSearch,
  authenticatedRequest: AxiosInstance,
  locationId?: string | number,
): Promise<IObservationIdentifierResponse> => {
  if (!query.o?.length) {
    delete query.o;
  }
  const parsedQuery = querystring.stringify({
    ...query,
  });
  const url = locationId
    ? `/api/v2/knownlocations/${locationId}/datadefinitions?${parsedQuery}`
    : `/api/v2/knownlocations/datadefinitions?${parsedQuery}`;

  const result = await authenticatedRequest.get(url);
  return mapToObservationIdentifierResponse(result.data);
};

const deleteKLObservationIdentifier = async (
  dataDefinitionId: string,
  authenticatedRequest: AxiosInstance,
) =>
  authenticatedRequest.delete(
    `/api/v2/knownlocations/datadefinitions/${dataDefinitionId}`,
  );

const updateKLObservationIdentifier = async (
  dataDefinitionId: number | string,
  observationIdentifier: IKLObservationIdentifierNew,
  authenticatedRequest: AxiosInstance,
) => {
  const result = await authenticatedRequest.put<IKLObservationIdentifierNew>(
    `/api/v2/knownlocations/datadefinitions/${dataDefinitionId}`,
    JSON.stringify(observationIdentifier),
    {
      headers: { "Content-Type": "application/json" },
    },
  );
  return result.data;
};

const createObservationIdentifiers = async (
  locationId: string,
  values: IObservationIndetifiersBulkNew,
  authenticatedRequest: AxiosInstance,
) => {
  const result =
    await authenticatedRequest.post<IObservationIndetifiersBulkNew>(
      `/api/v2/knownlocations/${locationId}/datadefinitions`,
      JSON.stringify(values),
      {
        headers: { "Content-Type": "application/json" },
      },
    );
  return result.data;
};

export {
  getObservationIdentifiers,
  deleteKLObservationIdentifier,
  updateKLObservationIdentifier,
  createObservationIdentifiers,
  resolveEditError,
  resolveCreateError,
};
